<script lang="ts" setup>
    import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';

    const props = defineProps<{
        controller: ReturnType<typeof useModal>;
        buttonText?: string;
    }>();

    const { acceptConsent } = useUsercentricsConsent('question_pro');

    const targetRef = useTemplateRef('target');
    const { activate, deactivate } = useFocusTrap(targetRef);

    const modalController = props.controller;

    watch(modalController.isOpen, (isOpen) => {
        if (isOpen) {
            requestAnimationFrame(() => {
                activate();
            });
        } else {
            deactivate();
        }
    });

    const handleAcceptConsent = async () => {
        const hasAcceptedConsent = await acceptConsent();

        if (hasAcceptedConsent) modalController.close();
    };
</script>

<template>
    <SharedModal
        :controller="modalController"
        class="survey-modal"
        wide
    >
        <div
            ref="target"
            class="container"
        >
            <ConsentMessage service-key="question_pro" />

            <PrimaryButton
                :button-text="buttonText ?? $t('genericCookieConsent.buttonTextNoConsent')"
                size="medium"
                native-button
                autofocus
                @click="handleAcceptConsent"
            />
        </div>

        <template #outside-scroll>
            <GradientShape
                theme="purple"
                class="gradient-shape"
                gradient-angle="225deg"
            />
        </template>
    </SharedModal>
</template>

<style lang="scss" scoped>
    :global(.survey-modal .content) {
        --shared-modal-content-background-color: var(--survey-consent-modal-background-color);

        position: relative;
        overflow: hidden;
        padding: var(--spacing) !important;

        @media (min-width: $br-m) {
            padding: var(--spacing--4) !important;
        }
    }

    :global(.survey-modal .content-scroll) {
        padding-bottom: var(
            --spacing--05
        ) !important; // Gives some extra space for the focus outline of the primary button so that the bottom doesn't get cut off
    }

    .container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing);
        text-align: center;
        position: relative;
        z-index: 1;
    }

    .gradient-shape {
        position: absolute;
        left: 0;
        bottom: 0;
        transform: translate(-20%, 80%);
        height: 40rem; // Some arbitrary value to match the Figma design
    }
</style>
